/**
 * El panel que baja del buscador mientras se escribe (BUS-02).
 *
 * Solo pinta: no pide datos, no maneja el teclado y no decide cuando se abre.
 * Todo eso vive en `Buscador`, que es quien tiene el input. Aca se recibe la
 * lista y cual esta marcada.
 *
 * Cada fila es un ENLACE de verdad y no un `div` con `onClick`: son rutas del
 * catalogo, y tienen que poder abrirse en otra pestana, copiarse y verse en la
 * barra de estado del navegador (CLAUDE.md, seccion 8). Llevan ademas
 * `role="option"` porque, para un lector de pantalla, esto es la lista de un
 * combobox; el patron ARIA preferiria que no fueran enlaces, pero perder la
 * navegacion real de un catalogo es peor que el compromiso.
 */
import { Link } from 'react-router-dom';

import type { SugerenciasBusqueda } from '@bandu/compartido';

import { Icono } from '../comunes/Icono';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';
import { opcionesDe } from '../../utilidades/sugerencias';
import { whatsappBusqueda } from '../../utilidades/whatsapp';

/**
 * El texto con lo buscado resaltado.
 *
 * Se parte la cadena y se arma con `<mark>`, sin `dangerouslySetInnerHTML`: el
 * nombre viene de la base y meterlo como HTML seria abrir una inyeccion para
 * ahorrarse cuatro lineas.
 */
function Resaltado({ texto, buscado }: { texto: string; buscado: string }) {
  const aguja = buscado.trim().toLowerCase();
  if (aguja.length === 0) return <>{texto}</>;

  const desde = texto.toLowerCase().indexOf(aguja);
  if (desde === -1) return <>{texto}</>;

  return (
    <>
      {texto.slice(0, desde)}
      <mark>{texto.slice(desde, desde + aguja.length)}</mark>
      {texto.slice(desde + aguja.length)}
    </>
  );
}

export interface PanelSugerenciasProps {
  sugerencias: SugerenciasBusqueda;
  /** Lo que hay escrito en la caja, para resaltarlo en los nombres. */
  escrito: string;
  id: string;
  /** Indice de la fila marcada con el teclado, o -1 si no hay ninguna. */
  marcada: number;
  /** Id de cada fila, compartido con el `aria-activedescendant` del input. */
  idOpcion: (indice: number) => string;
  alElegir: () => void;
}

export function PanelSugerencias({
  sugerencias,
  escrito,
  id,
  marcada,
  idOpcion,
  alElegir,
}: PanelSugerenciasProps) {
  const opciones = opcionesDe(sugerencias);
  const sinNada = opciones.length === 0;

  // El indice corre a traves de las tres secciones, no dentro de cada una.
  let indice = -1;
  const siguiente = () => {
    indice += 1;
    return indice;
  };

  /** Atributos comunes de una fila navegable con el teclado. */
  const fila = (posicion: number) => ({
    role: 'option' as const,
    id: idOpcion(posicion),
    'aria-selected': posicion === marcada,
    onClick: alElegir,
  });

  return (
    <div className="sugerencias">
      {sugerencias.terminoCorregido !== null ? (
        <div className="sug-correccion">
          <Icono nombre="rayo" />
          <span>
            Te mostramos <b>«{sugerencias.terminoCorregido}»</b>
          </span>
        </div>
      ) : null}

      {/*
        El `listbox` envuelve SOLO las filas navegables. El aviso de correccion
        y el pie son enlaces y textos que no son opciones de la lista, y
        colgarlos de un `listbox` le mentiria al lector de pantalla sobre
        cuantas opciones hay.
      */}
      <div id={id} role="listbox" aria-label="Sugerencias de busqueda">
        {sugerencias.productos.length > 0 ? (
          <div className="sug-seccion" role="group" aria-label="Productos">
            <div className="sug-titulo" role="presentation">
              Productos
            </div>
            {sugerencias.productos.map((producto) => {
              const posicion = siguiente();
              return (
                <Link
                  key={producto.id}
                  className={`sug-fila${posicion === marcada ? ' marcada' : ''}`}
                  to={rutas.producto(producto.slug)}
                  {...fila(posicion)}
                >
                  {producto.imagen !== null ? (
                    <img
                      className="sug-mini"
                      src={producto.imagen.url}
                      alt=""
                    />
                  ) : (
                    <span className="sug-mini" />
                  )}
                  <span className="sug-cuerpo">
                    <span className="sug-n">
                      <Resaltado texto={producto.nombre} buscado={escrito} />
                    </span>
                    <span className="sug-meta">
                      {producto.marca !== null ? (
                        <span>{producto.marca}</span>
                      ) : null}
                      <span className="mono">{producto.sku}</span>
                    </span>
                  </span>
                  <span className="sug-precio">{pesos(producto.precio)}</span>
                </Link>
              );
            })}
          </div>
        ) : null}

        {sugerencias.categorias.length > 0 ? (
          <div className="sug-seccion" role="group" aria-label="Categorias">
            <div className="sug-titulo" role="presentation">
              Categorias
            </div>
            {sugerencias.categorias.map((categoria) => {
              const posicion = siguiente();
              return (
                <Link
                  key={categoria.slug}
                  className={`sug-chip${posicion === marcada ? ' marcada' : ''}`}
                  to={rutas.categoria(categoria.slug)}
                  {...fila(posicion)}
                >
                  <Icono nombre="lista" />
                  {categoria.nombre}
                  <span className="cuenta">{categoria.cantidad}</span>
                </Link>
              );
            })}
          </div>
        ) : null}

        {sugerencias.marcas.length > 0 ? (
          <div className="sug-seccion" role="group" aria-label="Marcas">
            <div className="sug-titulo" role="presentation">
              Marcas
            </div>
            {sugerencias.marcas.map((marca) => {
              const posicion = siguiente();
              return (
                <Link
                  key={marca}
                  className={`sug-chip${posicion === marcada ? ' marcada' : ''}`}
                  to={rutas.busqueda(marca)}
                  {...fila(posicion)}
                >
                  <Icono nombre="etiqueta" />
                  {marca}
                </Link>
              );
            })}
          </div>
        ) : null}
      </div>

      {/*
        Sin resultados el panel no se esconde: se convierte en la salida a
        WhatsApp (BUS-06). Es el momento exacto en que el comprador se esta por
        ir, y hacerlo esperar a la pagina de resultados para ofrecerle ayuda es
        regalar esa venta.
      */}
      {sinNada ? (
        <div className="sug-vacio">
          No tenemos ese producto en el catalogo.
          <br />
          <a
            className="btn btn-sm btn-wa"
            style={{ marginTop: '11px' }}
            href={whatsappBusqueda(escrito)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              registrarClicWhatsapp({ origen: 'busqueda', termino: escrito });
              alElegir();
            }}
          >
            <Icono nombre="wa" />
            Preguntar por WhatsApp
          </a>
        </div>
      ) : (
        <Link
          className="sug-pie"
          to={rutas.busqueda(escrito)}
          onClick={alElegir}
        >
          <span>
            Ver los <b>{sugerencias.total}</b> resultados
          </span>
          <Icono nombre="flecha" />
        </Link>
      )}
    </div>
  );
}
